import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { enUS, tr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  CircularProgress,
  Divider,
  Badge,
  Button,
  alpha,
  useTheme,
} from '@mui/material';
import { FiberManualRecord as OnlineIcon } from '@mui/icons-material';
import { Email as RequestIcon } from '@mui/icons-material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { Chats } from '../../services/chatService';
import PageShell, { SurfaceCard } from '../../components/common/PageShell';
import VerifiedBadge from '../../components/common/VerifiedBadge';

const ChatRoomList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [chatRooms, setChatRooms] = useState([]);
  const [chatRequestCount, setChatRequestCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 10;

  const formatDate = (dateString) => {
    try {
      const date = parseISO(dateString);
      const currentLocale = i18n.language === 'tr' ? tr : enUS;
      return format(date, 'PPP p', { locale: currentLocale });
    } catch {
      return '';
    }
  };

  const fetchChatRooms = async (page = 1, append = false) => {
    if (isLoading || loadingMore) return;
    if (append) setLoadingMore(true);
    else setIsLoading(true);
    try {
      const response = await Chats({ pageIndex: page, pageSize: PAGE_SIZE });
      if (response.statusCode === 200) {
        const payload = response.data;
        const items = payload?.items ?? payload?.Items ?? (Array.isArray(payload) ? payload : []);
        const hasNext =
          payload?.hasNextPage ??
          payload?.HasNextPage ??
          (payload?.pageIndex ?? payload?.PageIndex ?? page) <
            (payload?.totalPages ?? payload?.TotalPages ?? 1);
        setChatRooms((prev) => (append ? [...prev, ...items] : items));
        setPageIndex(page);
        setHasMore(Boolean(hasNext));
        setChatRequestCount(response.count || 0);
      }
    } catch (error) {
      console.error('Error fetching chat rooms:', error);
    } finally {
      setIsLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchChatRooms(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca açılışta
  }, []);

  const loadMore = () => {
    if (!hasMore || loadingMore || isLoading) return;
    fetchChatRooms(pageIndex + 1, true);
  };
  return (
    <PageShell
      maxWidth="md"
      title={t('chat.title')}
      subtitle={t('chat.subtitle') || t('chat.noChats')}
      icon={<ChatBubbleOutlineIcon />}
      actions={
        <Button
          variant="outlined"
          startIcon={<RequestIcon />}
          onClick={() => navigate('/chat/requests')}
        >
          {t('chat.requests')}
          {chatRequestCount > 0 ? ` (${chatRequestCount})` : ''}
        </Button>
      }
    >
      {isLoading && chatRooms.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </Box>
      ) : (
        <SurfaceCard>
          <List sx={{ width: '100%', py: 0 }}>
            {chatRooms.map((room, index) => (
              <React.Fragment key={room.id}>
                <ListItem
                  alignItems="flex-start"
                  button
                  onClick={() =>
                    navigate(`/chat/${room.id}`, {
                      state: {
                        userName: room.atUserName,
                        isActive: room.atUserIsActive,
                        isIdentityVerified: room.atUserIsIdentityVerified === true,
                      },
                    })
                  }
                  sx={{
                    py: 2,
                    px: { xs: 2, md: 2.5 },
                    transition: 'background-color 0.15s ease',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      badgeContent={
                        room.atUserIsActive ? (
                          <OnlineIcon
                            sx={{
                              fontSize: 12,
                              color: 'success.main',
                              bgcolor: 'background.paper',
                              borderRadius: '50%',
                            }}
                          />
                        ) : null
                      }
                    >
                      <Avatar
                        src="https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png"
                        alt={room.atUserName}
                        sx={{
                          width: 48,
                          height: 48,
                          border: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                        }}
                      />
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        component="span"
                        variant="subtitle1"
                        fontWeight={room.newActive ? 700 : 600}
                      >
                        {room.atUserName}
                        {room.atUserIsIdentityVerified === true && (
                          <>
                            {' '}
                            <VerifiedBadge verified size={16} focusable={false} />
                          </>
                        )}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color={room.newActive ? 'primary' : 'text.secondary'}
                          fontWeight={room.newActive ? 600 : 400}
                          sx={{
                            display: 'block',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {room.lastMessage}
                        </Typography>
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 0.4 }}
                        >
                          {formatDate(room.lastAt)}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
                {index < chatRooms.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>

          {chatRooms.length === 0 && !isLoading && (
            <Box textAlign="center" py={6} px={2}>
              <Typography color="text.secondary">{t('chat.noChats')}</Typography>
            </Box>
          )}

          {hasMore && (
            <Box textAlign="center" py={2}>
              <Button variant="text" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? <CircularProgress size={20} /> : t('common.loadMore', 'Daha fazla')}
              </Button>
            </Box>
          )}
        </SurfaceCard>
      )}
    </PageShell>
  );
};

export default ChatRoomList;
